import React from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../hooks/products/types";

interface PromoItem {
  title: string;
  subtitle: string;
  image: string;
  bg: string;
  link?: string;
  price?: string;
  button?: string;
}

interface PromotionsProps {
  variant?: "one" | "two" | "three";
  products?: Product[];
}


// Renders an internal <Link> or external <a> based on the URL
const SmartLink: React.FC<{
  link?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ link, className, children }) => {
  const href = link || "/products";
  if (href.startsWith("http")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

const Promotions: React.FC<PromotionsProps> = ({
  variant = "one",
  products,
}) => {
  const data: PromoItem[] = products
    ? products.map((p, i) => ({
        title: p.name,
        subtitle: p.description
          ? p.description.slice(0, 60) + "..."
          : "Check out this amazing product!",
        image: p.image,
        bg: ["bg-blue-100", "bg-red-100", "bg-green-100"][i % 3],
        price: p.price ? `$${p.price.toFixed(2)}` : undefined,
        button: "Shop Now",
        link: `/product/${p.id}`,
      }))
    : [];

  const displayData: PromoItem[] =
    variant === "two"
      ? data.slice(0, 1)
      : variant === "three"
        ? data.slice(0, 2)
        : data;

  if (!displayData || displayData.length === 0) return null;

  // Variant Two — Hero: single large card, text left, image right
  if (variant === "two") {
    const promo = displayData[0];
    if (!promo) return null;
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="relative rounded-2xl overflow-hidden min-h-105 shadow-xl bg-gray-50">
          <img
            src={promo.image}
            alt={promo.title}
            className="absolute inset-0 w-full h-full object-contain"
          />
          {/* Left-to-right gradient so text is readable */}
          <div className="absolute inset-0 bg-linear-to-r from-black/85 via-black/55 to-transparent" />

          {/* Content — left-anchored on desktop, stacked on mobile */}
          <div className="relative z-10 flex flex-col justify-center h-full min-h-105 px-8 py-12 md:max-w-[55%]">
            {promo.title && (
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-3 drop-shadow-lg">
                {promo.title}
              </h2>
            )}
            {promo.subtitle && (
              <p className="text-white/80 text-lg mb-6 leading-relaxed">
                {promo.subtitle}
              </p>
            )}
            <SmartLink
              link={promo.link}
              className="inline-flex items-center gap-2 text-accent font-bold text-lg hover:gap-4 transition-all duration-200 group/cta"
            >
              Shop Now
              <span className="transition-transform duration-200 group-hover/cta:translate-x-1">→</span>
            </SmartLink>
          </div>
        </div>
      </div>
    );
  }

  // Variant Three — 2 cards, centered light overlay
  if (variant === "three") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4 max-w-7xl mx-auto cursor-pointer">
        {displayData.map((promo, idx) => (
          <div
            key={idx}
            className="relative rounded-2xl overflow-hidden min-h-70 shadow-lg group bg-gray-50"
          >
            <img
              src={promo.image}
              alt={promo.title}
              className="absolute inset-0 w-full h-full object-contain"
            />
            {/* Light frosted overlay */}
            <div className="absolute inset-0 bg-white/30 group-hover:bg-white/40 transition-colors duration-300" />

            <div className="relative z-10 flex flex-col items-center justify-center text-center h-full min-h-70 px-8 py-10">
              {promo.title && (
                <h2 className="text-xl md:text-2xl font-extrabold text-accent mb-2 drop-shadow-sm line-clamp-2">
                  {promo.title}
                </h2>
              )}
              {promo.subtitle && (
                <p className="text-gray-800 text-base mb-5 font-medium line-clamp-2">
                  {promo.subtitle}
                </p>
              )}
              <SmartLink
                link={promo.link}
                className="inline-flex items-center gap-2 text-accent font-bold text-base hover:gap-3 transition-all duration-200 group/cta"
              >
                Shop Now
                <span className="transition-transform duration-200 group-hover/cta:translate-x-1">→</span>
              </SmartLink>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Variant One — 3-col grid, hover-reveal text (always visible on mobile)
  if (variant === "one") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-7xl px-4 mx-auto">
        {displayData.map((promo, idx) => (
          <div
            key={idx}
            className="relative rounded-2xl overflow-hidden min-h-70 shadow-lg group bg-gray-50"
          >
            <img
              src={promo.image}
              alt={promo.title}
              className="absolute inset-0 w-full h-full object-contain"
            />
            {/* Bottom-up gradient overlay:
                mobile → always visible; desktop → fades in on hover */}
            <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/40 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300" />

            {/* Text layer:
                mobile → always visible; desktop → slides up on hover */}
            <div className="absolute bottom-0 left-0 right-0 z-10 px-6 py-6 translate-y-0 md:translate-y-4 md:group-hover:translate-y-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300">
              {promo.title && (
                <h2 className="text-xl font-extrabold text-white mb-1 line-clamp-2 drop-shadow">
                  {promo.title}
                </h2>
              )}
              {promo.subtitle && (
                <p className="text-white/80 text-sm mb-3 line-clamp-2">
                  {promo.subtitle}
                </p>
              )}
              <SmartLink
                link={promo.link}
                className="inline-flex items-center gap-1.5 text-accent font-bold text-sm hover:gap-2.5 transition-all duration-200 group/cta"
              >
                Shop Now
                <span className="transition-transform duration-200 group-hover/cta:translate-x-0.5">→</span>
              </SmartLink>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default Promotions;
